<template>
  <div class="ai-detail-views">
    <nav aria-label="操作结果详情"><button v-for="entry in card.entries" :key="entry.id" class="detail-link" :class="{selected: active === entry.id}" :disabled="permanentlyUnavailable(entry)" :aria-disabled="unavailable(entry) ? 'true' : 'false'" :aria-expanded="active === entry.id" @click="toggle(entry)">{{ active === entry.id ? entry.collapseLabel : entry.label }}</button></nav>
    <section v-if="active" class="detail-panel" aria-live="polite">
      <p v-if="loading" role="status">正在读取…</p>
      <p v-else-if="error" role="alert">{{ error }} <button class="detail-link" @click="retry">重试</button></p>
      <component v-else-if="content" :is="renderers[content.type]" :card="content" :read-only="readOnly || simulation || !['device_cards','scene_cards'].includes(content.type)" :blocked="blocked || simulation" :busy="busy" :now="now" />
    </section>
  </div>
</template>
<script>
import behavior from './mixins/card-behavior'
import renderers from './detail-renderers'
import { readDetail } from '@/ai/services/detail-sources'
import { createAiClient, identity } from '@/ai/services/api'
import { validateDetailContent } from '@/ai/protocol/detail-views'
// Entry labels and child content come from the server; never branch on button text or entry id.
// Bounded disclosure: one active entry, allowlisted child components, no caller-selected URL.
// Closing destroys child subscriptions/popups. Reopening a remote entry always refreshes it.
export default {
  mixins: [behavior],
  computed: { renderers() { return renderers } },
  data: () => ({ active: null, content: null, loading: false, error: '', simulation: false, scope: '' }),
  mounted() { this._alive = true; this._generation = 0; this.scope = identity() },
  beforeDestroy() { this._alive = false; this.closeRequest() },
  watch: { card() { this.reset() } },
  methods: {
    permanentlyUnavailable(entry) { return !!entry.source && (this.readOnly || this.blocked || this.expired || this.scope !== identity()) },
    unavailable(entry) { return !!entry.source && (this.disabled || this.scope !== identity()) },
    closeRequest() { this._generation++; if (this._client) this._client.close(); this._client = null },
    reset() { this.closeRequest(); this.active = null; this.content = null; this.loading = false; this.error = '' },
    async toggle(entry) {
      if (this.active === entry.id) { this.reset(); return }
      if (this.unavailable(entry)) {
        if (!this.permanentlyUnavailable(entry) && (!this._noticeAt || Date.now() - this._noticeAt > 2500)) {
          this._noticeAt = Date.now()
          this.$toast('正在处理当前操作，请稍后再试。')
        }
        return
      }
      this.reset(); this.active = entry.id; this.simulation = !!entry.content && ['device_cards','scene_cards'].includes(entry.content.type)
      if (entry.content) { this.content = validateDetailContent(entry.content); return }
      const generation = this._generation; this._client = createAiClient(); this.loading = true
      try {
        const result = await readDetail(this._client, entry.source)
        if (this._alive && generation === this._generation && this.scope === identity()) this.content = validateDetailContent(result)
      } catch (e) { if (this._alive && generation === this._generation) this.error = '内容读取失败，请重试。' }
      finally { if (this._alive && generation === this._generation) this.loading = false }
    },
    retry() { const entry = this.card.entries.find(e => e.id === this.active); if (entry) { this.reset(); this.toggle(entry) } }
  }
}
</script>
<style scoped>
.ai-detail-views{width:100%;min-width:0;box-sizing:border-box}
nav{display:flex;flex-wrap:wrap;gap:8px 22px;align-items:center}
.detail-link{display:inline-flex;align-items:center;gap:6px;border:0;background:none;padding:5px 0;color:#2563c7;font:inherit;font-size:13px;cursor:pointer}
.detail-link.selected{color:#174b91}
.detail-link:disabled{color:#8793a5;cursor:default}.detail-link:focus-visible{outline:2px solid #3975df;outline-offset:3px}
/* The outer AiCard supplies the sole border/padding. Children get exactly the same width
   as standalone cards; never animate width/height or add another horizontal inset here. */
.detail-panel{width:100%;box-sizing:border-box;margin-top:14px;padding:0;border:0;min-width:0;animation:detail-enter .16s ease-out}
p{font-size:13px;color:#637185}
@keyframes detail-enter{from{opacity:0}to{opacity:1}}
@media(prefers-reduced-motion:reduce){.detail-panel{animation:none}}
</style>
